import React, { useContext, useState } from "react";
import {
  Modal,
  Slider,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  TextField,
} from "@mui/material";
import { styled, useTheme, useMediaQuery } from "@mui/system";
import { motion } from "framer-motion";
import GlobalStateContext from "../context/globalStateContext";
import { COLORS } from "../utils/COLORS";
import { functions, firestore } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { firebase } from "../firebaseConfig";
import UserContext from "../context/userContext";
import { toast } from "react-simple-toasts";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import * as amplitude from '@amplitude/analytics-browser';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#0F172A",
  borderRadius: "14px",
  boxShadow: theme.shadows[5],
  padding: "32px 24px",
  outline: "none",
  width: "90%",
  maxWidth: "420px",
  maxHeight: "80vh",
  overflowY: "auto",
  textAlign: "center",
  border: "1px solid #1E293B",
}));

const StatsBadge = styled(Box)({
  backgroundColor: "#1F2937",
  borderRadius: "8px",
  padding: "12px 16px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
});

const DonationAmounts = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: "6px",
  marginBottom: "14px",
});

const AmountButton = styled(Button)(({ selected }) => ({
  backgroundColor: selected ? "#10B981" : "#1E293B",
  color: "#FFFFFF",
  border: selected ? "1px solid #10B981" : "1px solid #334155",
  borderRadius: "8px",
  padding: "8px 6px",
  fontSize: "13px",
  fontWeight: "500",
  textTransform: "none",
  minWidth: "auto",
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? "#059669" : "#334155",
    transform: "translateY(-1px)",
  },
}));

const AmountTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#1E293B",
    border: "1px solid #334155",
    borderRadius: "8px",
    height: "100%",
    color: "#FFFFFF",
    fontSize: "13px",
    "& fieldset": {
      border: "none",
    },
    "&:hover": {
      backgroundColor: "#334155",
    },
    "&.Mui-focused": {
      backgroundColor: "#334155",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 6px",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: "500",
    color: "#FFFFFF",
    "&::placeholder": {
      color: "#9CA3AF",
      opacity: 1,
    },
  },
});

const SelectedAmountDisplay = styled(Typography)({
  fontSize: "28px",
  fontWeight: "700",
  color: "#FFFFFF",
  minWidth: "80px",
});

const StyledSlider = styled(Slider)({
  flex: 1,
  color: "#10B981",
  height: 4,
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#10B981",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#374151",
    opacity: 1,
  },
  "& .MuiSlider-thumb": {
    height: 16,
    width: 16,
    backgroundColor: "#10B981",
    border: "2px solid #FFFFFF",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      backgroundColor: "#10B981",
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    },
    "&:before": {
      display: "none",
    },
  },
});

const AmountRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "16px",
});

const ShareButtonsContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px",
  marginBottom: "20px",
});

const ShareButton = styled(Button)(({ socialplatform }) => {
  const colors = {
    facebook: "#1877F2",
    twitter: "#1DA1F2",
    linkedin: "#0A66C2",
    whatsapp: "#25D366",
    copy: "#10B981",
  };

  return {
    backgroundColor: colors[socialplatform] || "#10B981",
    color: "#FFFFFF",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: colors[socialplatform] || "#059669",
      transform: "translateY(-1px)",
    },
  };
});

const SubModal = () => {
  const { user, setUser } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [pledgeAmount, setPledgeAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showSharingStep, setShowSharingStep] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const donationAmounts = [10, 25, 100, 250];

  const handleClose = () => {
    setShowSharingStep(false);
    setGlobalState({ ...globalState, showSubModal: false });
  };

  const handleSliderChange = (event, newValue) => {
    setPledgeAmount(newValue);
  };

  const shareUrl = `${window.location.origin}/dog/${globalState.selectedDog?.slug}`;
  const shareText = `Help save ${globalState.selectedDog?.name}! This sweet dog needs our help to find a rescue. Every share increases their chance of survival. 🐕❤️`;

  const handleFacebookShare = () => {
    // Track individual share event
    amplitude.track('share', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareMethod: 'facebook',
      location: 'subscription_modal'
    });
    
    // Track general shares event
    amplitude.track('shares', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareType: 'facebook_modal'
    });

    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleTwitterShare = () => {
    // Track individual share event
    amplitude.track('share', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareMethod: 'twitter',
      location: 'subscription_modal'
    });
    
    // Track general shares event
    amplitude.track('shares', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareType: 'twitter_modal'
    });

    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleLinkedInShare = () => {
    // Track individual share event
    amplitude.track('share', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareMethod: 'linkedin',
      location: 'subscription_modal'
    });
    
    // Track general shares event
    amplitude.track('shares', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareType: 'linkedin_modal'
    });

    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl
    )}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleCopyLink = () => {
    // Track individual share event
    amplitude.track('share', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareMethod: 'copy_link',
      location: 'subscription_modal'
    });
    
    // Track general shares event
    amplitude.track('shares', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareType: 'copy_link_modal'
    });

    console.log(shareUrl);
    navigator.clipboard.writeText(shareUrl);
    toast("Link copied to clipboard!");
  };

  const handleWhatsAppShare = () => {
    // Track individual share event
    amplitude.track('share', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareMethod: 'whatsapp',
      location: 'subscription_modal'
    });
    
    // Track general shares event
    amplitude.track('shares', {
      dogName: globalState.selectedDog?.name,
      dogSlug: globalState.selectedDog?.slug,
      shareType: 'whatsapp_modal'
    });

    const url = `https://wa.me/?text=${encodeURIComponent(
      shareText + " " + shareUrl
    )}`;
    window.open(url, "_blank");
  };

  const handleConfirmSubscription = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const createSubscription = httpsCallable(functions, "createSubscription");
      const result = await createSubscription({
        email: user.email,
        amount: pledgeAmount, // Dollar amount, not cents
        paymentMethodId: user.stripePaymentMethodId,
        dogSlug: globalState.selectedDog?.slug,
        dogName: globalState.selectedDog?.name,
      });

      const { success, subscriptionId, stripeSubscriptionId, status } =
        result.data;

      if (success) {
        // Save subscription data to user's subscriptions in Firestore
        await firestore
          .collection("users")
          .doc(user.uid)
          .collection("subscriptions")
          .doc(subscriptionId)
          .set({
            subscriptionId: subscriptionId,
            stripeSubscriptionId: stripeSubscriptionId,
            amount: pledgeAmount,
            status: status,
            dogSlug: globalState.selectedDog?.slug,
            dogName: globalState.selectedDog?.name,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          });

        // Track subscription event
        amplitude.track('subscription', {
          dogName: globalState.selectedDog?.name,
          dogSlug: globalState.selectedDog?.slug,
          subscriptionAmount: pledgeAmount,
          subscriptionId: subscriptionId,
          userId: user.email
        });

        setShowSharingStep(true);
        toast("Subscription created successfully!");
      } else {
        throw new Error("Failed to create subscription");
      }
    } catch (err) {
      setError(err.message);
      console.error("Subscription error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <StyledModal
      open={globalState.showSubModal}
      onClose={handleClose}
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        onClick={handleClose}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: window.innerWidth > 600 ? "center" : "flex-end",
          height: "100%",
        }}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {!showSharingStep ? (
            <>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "8px",
                }}
              >
                ⚡ Wait.. before you go!
              </Typography>

              <StatsBadge>
                <NotificationsIcon
                  sx={{ color: "#10B981", fontSize: "20px" }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#FFFFFF",
                    fontWeight: "600",
                  }}
                >
                  Dogs with subscribers are 9x more likely to be saved
                </Typography>
              </StatsBadge>

              {/* <Typography sx={{ 
            fontSize: "14px",
            color: "#9CA3AF",
            marginBottom: "24px",
            lineHeight: "1.5"
          }}>
            90% goes to rescue org. 10% PledgeFast. Cancel anytime.
          </Typography> */}

              {/* <AmountRow>
            <SelectedAmountDisplay>
              ${pledgeAmount}
              <span style={{ fontSize: "16px", fontWeight: "400", color: "#9CA3AF" }}>/mo</span>
            </SelectedAmountDisplay>
            <StyledSlider
              value={pledgeAmount}
              min={10}
              max={200}
              onChange={(event, newValue) => {
                setPledgeAmount(newValue);
              }}
              valueLabelDisplay="off"
            />
          </AmountRow> */}

              <DonationAmounts>
                {donationAmounts.map((amount) => (
                  <AmountButton
                    key={amount}
                    selected={pledgeAmount === amount && customAmount === ""}
                    onClick={() => {
                      setPledgeAmount(amount);
                      setCustomAmount("");
                    }}
                  >
                    ${amount}/mo
                  </AmountButton>
                ))}
                <AmountTextField
                  placeholder="Custom"
                  value={customAmount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setCustomAmount(value);
                    if (value && parseInt(value) > 0) {
                      setPledgeAmount(parseInt(value));
                    }
                  }}
                  onFocus={() => {
                    if (!customAmount) {
                      setCustomAmount("");
                    }
                  }}
                  autoComplete="off"
                />
              </DonationAmounts>

              {/* <Typography sx={{ 
            fontSize: "11px", 
            color: "#6B7280", 
            fontWeight: "400",
            marginBottom: "20px"
          }}>
            💯 Monthly pledges unlock instant alerts for new dogs in your area. You're only charged when a rescue uploads proof.
          </Typography> */}

              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  fontWeight: "400",
                  marginBottom: "16px",
                  textAlign: "center",
                }}
              >
                {/* If {globalState.selectedDog?.name} is rescued - your first
                charge will be 30 days after rescue.85% goes to rescues, 15%
                helps us save more dogs. Cancel anytime. */}
                If {globalState.selectedDog?.name} is rescued - your first
                charge will be 30 days after rescue. A 15% platform fee helps us
                operate as our only fee. Cancel anytime.
              </Typography>

              <Button
                variant="contained"
                fullWidth
                startIcon={<FavoriteIcon />}
                onClick={handleConfirmSubscription}
                disabled={isProcessing}
                sx={{
                  backgroundColor: "#10B981",
                  color: "#FFFFFF",
                  borderRadius: "10px",
                  padding: "12px",
                  fontSize: "15px",
                  fontWeight: "600",
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#059669",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
                  },
                }}
              >
                {isProcessing ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: COLORS.primaryGreen }}
                  />
                ) : (
                  `Subscribe to ${globalState.selectedDog?.name}`
                )}
              </Button>

              <Button
                onClick={handleClose}
                sx={{
                  marginTop: "12px",
                  color: "#9CA3AF",
                  fontSize: "14px",
                  textTransform: "none",
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "transparent",
                  },
                }}
              >
                No, Thank You
              </Button>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "8px",
                }}
              >
                🎉 You're an angel
              </Typography>

              <StatsBadge>
                <ShareIcon sx={{ color: "#10B981", fontSize: "20px" }} />
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#FFFFFF",
                    fontWeight: "600",
                  }}
                >
                  Please share his page to find {globalState.selectedDog?.name}{" "}
                  a rescue
                </Typography>
              </StatsBadge>

              {/* <Typography sx={{ 
                fontSize: "16px",
                color: "#9CA3AF",
                marginBottom: "24px",
                textAlign: "center",
                lineHeight: "1.5"
              }}>
                Help {globalState.selectedDog?.name} find a rescue.
              </Typography> */}

              <ShareButtonsContainer>
                <ShareButton
                  socialplatform="facebook"
                  startIcon={<FacebookIcon />}
                  onClick={handleFacebookShare}
                >
                  Facebook
                </ShareButton>

                <ShareButton
                  socialplatform="twitter"
                  startIcon={<TwitterIcon />}
                  onClick={handleTwitterShare}
                >
                  Twitter
                </ShareButton>

                <ShareButton
                  socialplatform="whatsapp"
                  startIcon={<WhatsAppIcon />}
                  onClick={handleWhatsAppShare}
                >
                  WhatsApp
                </ShareButton>

                <ShareButton
                  socialplatform="linkedin"
                  startIcon={<LinkedInIcon />}
                  onClick={handleLinkedInShare}
                >
                  LinkedIn
                </ShareButton>

                <ShareButton
                  socialplatform="copy"
                  startIcon={<LinkIcon />}
                  onClick={handleCopyLink}
                  sx={{ gridColumn: "1 / -1" }}
                >
                  Copy Link
                </ShareButton>
              </ShareButtonsContainer>

              <Button
                onClick={handleClose}
                sx={{
                  marginTop: "8px",
                  color: "#9CA3AF",
                  fontSize: "14px",
                  textTransform: "none",
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "transparent",
                  },
                }}
              >
                I'll share later
              </Button>
            </>
          )}
        </ModalContent>
      </motion.div>
    </StyledModal>
  );
};

export default SubModal;
